import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { CreditCard, Search } from 'lucide-react';
import {
  Button,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER } from '@/lib/data-table';
import type { StripeDunningExclusionRow } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useDunningStripeExclusions } from '../hooks/useDunningReview';
import { DUNNING_CONTROL_ROLES } from '@/features/optihub/components/DunningExclusionControl';
import {
  DunningExclusionDialog,
  PAUSED,
  STOPPED,
  type DunningApplied,
  type DunningDialogMode,
  type DunningTarget,
} from '@/features/optihub/components/DunningExclusionDialog';
import { PagerNav, PagerSummary, useClientPage } from './TablePager';
import { CopyBtn } from '@/features/customers/components/CustomerFacts';
import { AccountStatusBadge, DunningFlagBadge, SubscriptionStatusBadge } from './StatusBadges';

/** How long typing must pause before the (live Stripe) search runs. */
const SEARCH_DEBOUNCE_MS = 350;

const TID = 'stripe-exclusions';

function labelOf(row: StripeDunningExclusionRow): string {
  return row.companyName || row.stripeName || row.ownerEmail || row.stripeEmail || row.stripeId;
}

/**
 * Dunning Review → "Dunning Exclusion List": every customer whose Stripe metadata
 * `staff-app-dunning-exclusion` is `'true'` (stopped) or `'pause'` (until
 * `staff-app-dunning-exclusion-until-date`). Requester, 2026-09-23.
 *
 * Read LIVE from Stripe — the flag's source of truth — so there is no copy to drift. One
 * search box for customer email, domain or Stripe id.
 *
 * EDITABLE here (requester, 2026-09-23) for ROOT + BILLING (DUNNING_CONTROL_ROLES, the same
 * rule as the mutation): per row (Change / Resume) and in BULK over ticked rows. Both open the
 * SAME `DunningExclusionDialog` the Billing Console uses, which writes one customer at a time
 * through the per-customer mutation — one Activity Log row each, and a failure on one does not
 * stop the rest.
 *
 * WHY THE LOCAL OVERRIDES. After a write, the row shows Stripe's READ-BACK immediately and a
 * resumed customer leaves the list — without waiting for Stripe's search index, which can
 * disagree with itself for a minute or more after a change (measured 2026-09-23) and would
 * otherwise put an edited row straight back as it was.
 *
 * staff-app's OWN ladder does not read this flag (it reads the Mongo `dunningExclusions` list,
 * whose section was removed from this page 2026-09-23); the copy says so.
 */
export function StripeExclusionsSection() {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(input), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);
  const { rows: fetched, loading, error } = useDunningStripeExclusions(search);

  const me = useAuthStore((s) => s.adminUser);
  const canEdit = !!me?.roles?.some((r) => DUNNING_CONTROL_ROLES.includes(r));

  // stripeId → what Stripe read back after OUR write, laid over the (lagging) search result.
  const [overrides, setOverrides] = useState<Map<string, DunningApplied>>(new Map());
  const rows = useMemo(
    () =>
      fetched
        .map((r) => {
          const o = overrides.get(r.stripeId);
          return o ? { ...r, value: o.value ?? '', untilDate: o.untilDate } : r;
        })
        .filter((r) => r.value === STOPPED || r.value === PAUSED),
    [fetched, overrides],
  );
  const page = useClientPage(rows, search);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => setSelected(new Set()), [search]);
  const pageIds = page.pageRows.map((r) => r.stripeId);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllOnPage = (on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const [dialog, setDialog] = useState<{
    mode: DunningDialogMode;
    targets: DunningTarget[];
  } | null>(null);
  const openFor = (mode: DunningDialogMode, list: StripeDunningExclusionRow[]) =>
    setDialog({ mode, targets: list.map((r) => ({ stripeId: r.stripeId, label: labelOf(r) })) });
  const selectedRows = rows.filter((r) => selected.has(r.stripeId));

  const onApplied = (applied: DunningApplied[]) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const a of applied) next.set(a.stripeId, a);
      return next;
    });
    setSelected((prev) => {
      const next = new Set(prev);
      for (const a of applied) next.delete(a.stripeId);
      return next;
    });
  };

  return (
    <div className="mt-10" data-testid={`${TID}-section`}>
      <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
        <CreditCard className="h-4 w-4" />
        {'Dunning Exclusion List'}
      </h2>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        {
          'Customers whose Stripe metadata staff-app-dunning-exclusion is true (stopped) or pause (until a date). Change them here or in OptiHub → Billing Console; the dunning tool that looks up Stripe reads the flag. staff-app’s own dunning ladder does not read this flag. Read from Stripe’s search, which can take a few minutes to catch up after a change made elsewhere — the Billing Console badge is always current.'
        }
      </p>

      {/* Same search box as the Dunning Review filter bar (DunningFilterBar): the same row
          wrapper, width bounds and icon placement, so the two tables on the page match. */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search email, domain or Stripe ID"
            className="pl-9"
            aria-label="Search excluded customers"
            data-testid={`${TID}-search`}
          />
        </div>
      </div>

      {error ? (
        <div className="mt-3 text-sm text-destructive" data-testid={`${TID}-error`}>
          {`Could not read Stripe: ${error.message}`}
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="mt-3 text-sm text-muted-foreground">{'Loading…'}</div>
      ) : rows.length === 0 ? (
        <div className="mt-3 text-sm text-muted-foreground" data-testid={`${TID}-empty`}>
          {search.trim()
            ? `No excluded customer matches "${search.trim()}".`
            : 'No customer is excluded in Stripe.'}
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {canEdit && selected.size > 0 ? (
              <div className="flex items-center gap-2" data-testid={`${TID}-bulk`}>
                <span
                  className="text-xs font-medium text-foreground"
                  data-testid={`${TID}-selected`}
                >
                  {`${selected.size} selected`}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openFor('exclude', selectedRows)}
                  data-testid={`${TID}-bulk-change`}
                >
                  {'Stop / Pause…'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openFor('resume', selectedRows)}
                  data-testid={`${TID}-bulk-resume`}
                >
                  {'Resume dunning'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(new Set())}
                  data-testid={`${TID}-bulk-clear`}
                >
                  {'Clear'}
                </Button>
              </div>
            ) : null}
            <div className="ml-auto">
              <PagerSummary page={page} testId={TID} />
            </div>
          </div>
          <div className="mt-2 overflow-x-auto rounded-md border">
            <Table className={DATA_TABLE_CELLS} data-testid={`${TID}-table`}>
              <TableHeader className={DATA_TABLE_HEADER}>
                <TableRow>
                  {canEdit ? (
                    <TableHead className="w-8">
                      <Checkbox
                        checked={allOnPage}
                        onCheckedChange={(v) => toggleAllOnPage(v === true)}
                        aria-label="Select every customer on this page"
                        data-testid={`${TID}-select-all`}
                      />
                    </TableHead>
                  ) : null}
                  <TableHead>{'Customer'}</TableHead>
                  <TableHead className="whitespace-nowrap">{'Billing email'}</TableHead>
                  <TableHead className="whitespace-nowrap">{'Account status'}</TableHead>
                  <TableHead className="whitespace-nowrap">{'Subscription status'}</TableHead>
                  <TableHead className="whitespace-nowrap">{'Dunning status'}</TableHead>
                  {canEdit ? <TableHead className="text-right">{'Action'}</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.pageRows.map((row) => (
                  <StripeExclusionRowView
                    key={row.stripeId}
                    row={row}
                    canEdit={canEdit}
                    checked={selected.has(row.stripeId)}
                    onToggle={() => toggle(row.stripeId)}
                    onChange={() => openFor('exclude', [row])}
                    onResume={() => openFor('resume', [row])}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <PagerNav page={page} testId={TID} />
        </>
      )}

      {canEdit ? (
        <DunningExclusionDialog
          mode={dialog?.mode ?? null}
          targets={dialog?.targets ?? []}
          onClose={() => setDialog(null)}
          onApplied={onApplied}
          testIdPrefix={`${TID}-edit`}
        />
      ) : null}
    </div>
  );
}

function StripeExclusionRowView({
  row,
  canEdit,
  checked,
  onToggle,
  onChange,
  onResume,
}: {
  row: StripeDunningExclusionRow;
  canEdit: boolean;
  checked: boolean;
  onToggle: () => void;
  onChange: () => void;
  onResume: () => void;
}) {
  const who = labelOf(row);
  return (
    <TableRow data-testid={`stripe-exclusion-row-${row.stripeId}`}>
      {canEdit ? (
        <TableCell className="w-8">
          <Checkbox
            checked={checked}
            onCheckedChange={onToggle}
            aria-label={`Select ${who}`}
            data-testid={`stripe-exclusion-select-${row.stripeId}`}
          />
        </TableCell>
      ) : null}
      {/* Name · Owner · Stripe customer id (requester, 2026-09-23 — no account id). The NAME
          links to the customer's page when an account names this Stripe customer, so dropping
          the visible account id loses no navigation. */}
      <TableCell>
        {row.accountId ? (
          <Link
            to={`/staff/customers/${row.accountId}`}
            className="block max-w-[240px] truncate font-medium text-foreground underline-offset-2 hover:underline"
            title={who}
            data-testid={`stripe-exclusion-name-${row.stripeId}`}
          >
            {who}
          </Link>
        ) : (
          <div
            className="max-w-[240px] truncate font-medium"
            title={who}
            data-testid={`stripe-exclusion-name-${row.stripeId}`}
          >
            {who}
          </div>
        )}
        {row.ownerEmail ? (
          <div className="text-xs text-muted-foreground">{`Owner: ${row.ownerEmail}`}</div>
        ) : null}
        <div className="font-mono text-xs text-muted-foreground">
          <span data-testid={`stripe-exclusion-id-${row.stripeId}`}>{row.stripeId}</span>
          <CopyBtn
            value={row.stripeId}
            label="Stripe ID"
            testId={`stripe-exclusion-copy-${row.stripeId}`}
          />
        </div>
      </TableCell>
      {/* The Stripe customer's own email — where Stripe sends this customer's invoices, i.e. the
          billing email (the Billing Console's Billing Email field). Not the owner's. */}
      <TableCell className="text-sm">{row.stripeEmail ?? '—'}</TableCell>
      {/* The same badges the customer page shows (CustomerFacts), so a status reads the same
          everywhere: Account = payment.isTrial + isActive; Subscription = Stripe's status of the
          customer's most urgent subscription. */}
      <TableCell>
        <AccountStatusBadge
          hasAccount={!!row.accountId}
          isActive={row.isActive}
          isTrial={row.isTrial}
          testId={`stripe-exclusion-account-${row.stripeId}`}
        />
      </TableCell>
      <TableCell>
        <SubscriptionStatusBadge
          status={row.subscriptionStatus}
          multiple={row.subscriptionMultiple}
          testId={`stripe-exclusion-subscription-${row.stripeId}`}
        />
      </TableCell>
      <TableCell>
        <DunningFlagBadge
          value={row.value}
          untilDate={row.untilDate}
          testId={`stripe-exclusion-status-${row.stripeId}`}
        />
      </TableCell>
      {canEdit ? (
        <TableCell>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onChange}
              data-testid={`stripe-exclusion-change-${row.stripeId}`}
            >
              {'Change'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onResume}
              data-testid={`stripe-exclusion-resume-${row.stripeId}`}
            >
              {'Resume'}
            </Button>
          </div>
        </TableCell>
      ) : null}
    </TableRow>
  );
}
